// Talk track types — mirror app/models/talk_track.py and the response shapes
// of app/api/studio_talk_tracks_router.py (camelCase).

import type { InputMode } from "../services/analytics";

export type VersionStatus = "draft" | "in_review" | "approved" | "published" | "retired";
export type Arm = "control" | "candidate" | "holdout";
export type CheckLevel = "error" | "warning";
export type SafetyRoute = "safety_core" | "medical" | "resources";
// What a screen asks about: asked once per conversation per risk across topics.
export type SafetyRisk = "self_harm" | "harm_to_others" | "medical" | "abuse" | "other";
export type FollowUp = "none" | "before_event" | "after_episode" | "daily";

export const SAFETY_ROUTES: SafetyRoute[] = ["safety_core", "medical", "resources"];
export const SAFETY_RISKS: SafetyRisk[] = ["self_harm", "harm_to_others", "medical", "abuse", "other"];
export const FOLLOW_UPS: FollowUp[] = ["none", "before_event", "after_episode", "daily"];

// Same pattern the backend enforces on every unit id.
export const UNIT_ID_PATTERN = /^[a-z0-9_.-]+$/;

export interface TrackQuestion {
  id: string;
  text: string;
  voiceText: string;
}

// ---- Introduction areas (topic kind "introduction"; app/services/talk_tracks/introduction.py)

export type TopicKind = "playbook" | "introduction";
export type AreaSensitivity = "normal" | "permission" | "never_explore";

export interface ProbeQuestion {
  id: string;
  // situation | feeling | impact | meaning | specific | choice | scale
  rung: string;
  text: string;
  voiceText: string;
}

export interface PermissionQuestion {
  text: string;
  voiceText: string;
  // Facts recorded on a "no" (closes the area as explored); empty = declined.
  onNo: Record<string, string>;
}

export interface AreaSlot {
  name: string;
  // A closed value set (validated in code); empty = free text.
  values: string[];
}

export interface IntroductionPolicy {
  reasonAreas: Record<string, string[]>;
  priors: Record<string, string[]>;
  maxAreas: number;
  anchorMaxQuestions: number;
  softCapTurns: number;
  softCapMinutes: number;
  hardCapTurns: number;
  scanQuestion: TrackQuestion;
}

export interface ExploreTrack {
  id: string;
  dimension: string;
  name: string;
  goal: string;
  fills: string[];
  useWhen: string;
  skipWhen: string;
  doneWhen: string;
  maxQuestions: number;
  rationale: string;
  questions: TrackQuestion[];
  // Introduction areas only (absent or empty on playbooks).
  area?: string | null;
  sensitivity?: AreaSensitivity;
  permissionQuestion?: PermissionQuestion | null;
  bridges?: string[];
  probes?: ProbeQuestion[];
  slots?: AreaSlot[];
  shortForm?: string;
}

export interface Explainer {
  id: string;
  concept: string;
  title: string;
  audience: string;
  text: string;
  voiceText: string;
  checkIn: string;
}

export interface TalkTrackModule {
  id: string;
  type: string;
  title: string;
  approach: string;
  requires: string[];
  neverWhen: string[];
  steps: string[];
  choiceVoice: string;
  options: string[];
  followUp: FollowUp;
  successSignal: string;
  mapsToProtocol: string | null;
}

export interface SafetyScreen {
  id: string;
  when: string;
  question: string;
  route: SafetyRoute;
  // Unset on older content: the backend infers it from the wording (and warns).
  risk?: SafetyRisk | null;
  note: string;
}

// A common shape of the topic's problem, reflected back tentatively once
// the guide has explored enough (the formulate step).
export interface PatternCard {
  id: string;
  name: string;
  signals: string[];
  reflection: string;
  explainerId: string | null;
  moduleId: string | null;
}

export interface TalkTrackContent {
  summary: string;
  entrySignals: string[];
  tracks: ExploreTrack[];
  explainers: Explainer[];
  modules: TalkTrackModule[];
  safetyScreens: SafetyScreen[];
  // Optional: versions authored before patterns existed don't carry them.
  patterns?: PatternCard[];
  bridge?: string;
  closings?: string[];
  // The introduction's policy (kind "introduction" only).
  introduction?: IntroductionPolicy | null;
}

export interface TalkTrackTopic {
  id: string;
  name: string;
  // Set at creation; one published introduction at a time.
  kind: TopicKind;
  category: string;
  description: string;
  sortOrder: number;
  enabled: boolean;
  requiresSafetyScreen: boolean;
  liveVersion: number | null;
  candidateVersion: number | null;
  rolloutPct: number;
  // Guide that owns this subject for hand-overs (null = Aura keeps it).
  ownerGuideId: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface TopicListItem extends TalkTrackTopic {
  draftVersion: number | null;
  inReviewVersion: number | null;
  approvedVersion: number | null;
  latestVersion: number | null;
}

export interface VersionSummary {
  topicId: string;
  version: number;
  status: VersionStatus;
  basedOn: number | null;
  authorUid: string | null;
  reviewerUid: string | null;
  reviewNote: string | null;
  submittedAt: string | null;
  approvedAt: string | null;
  publishedAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface TalkTrackVersion extends VersionSummary {
  content: TalkTrackContent;
  // Fingerprint of `content`; a simulation only counts for this exact hash.
  contentHash?: string | null;
}

export interface TopicDetail {
  topic: TalkTrackTopic;
  versions: VersionSummary[];
}

export interface ContentCheck {
  level: CheckLevel;
  code: string;
  message: string;
  unitId: string | null;
}

export interface TalkTrackComment {
  id: string;
  topicId: string;
  version: number;
  unitId: string | null;
  authorUid: string;
  body: string;
  resolved: boolean;
  createdAt: string | null;
}

export interface VersionDetail {
  version: TalkTrackVersion;
  checks: ContentCheck[];
  comments: TalkTrackComment[];
}

export interface TalkTrackMeta {
  uid: string;
  isReviewer: boolean;
  allowSelfApproval: boolean;
  // Off: a draft publishes directly; on: submit → approve → publish.
  requireReview: boolean;
  requireSimulation: boolean;
  // Users (not conversations) the candidate arm should have before
  // promoting on the numbers; the name predates that.
  minCallsToPromote: number;
  enabled: boolean;
  categories: string[];
  dimensions: string[];
  moduleTypes: string[];
  protocols: string[];
  kinds: TopicKind[];
  lifeAreas: string[];
  sensitivities: AreaSensitivity[];
  probeRungs: string[];
}

// ---- performance (app/services/talk_tracks/stats.py)

export interface OutcomeRates {
  moves: number;
  questions: number;
  offers: number;
  // Replies nobody classified (director timeout/failure/merged): excluded
  // from the answer and deflect rates.
  unclassified?: number;
  answerRate: number | null;
  dropOffRate: number | null;
  deflectRate: number | null;
  offerAcceptance: number | null;
}

export type RateKey = "answerRate" | "dropOffRate" | "deflectRate" | "offerAcceptance";

// 95% range over users (mean of per-user rates ± 1.96 standard errors,
// clamped to 0–1); null below two users.
export interface RateRange {
  low: number;
  high: number;
  // Users this rate comes from (often fewer than the row's).
  users: number;
}

// Topic/version rates weigh each user equally; `pooled` is the
// move-weighted version. `topUserShare`: the most active user's share of moves.
export interface BalancedRates extends OutcomeRates {
  topUserShare: number | null;
  pooled: OutcomeRates;
  ci?: Partial<Record<RateKey, RateRange | null>>;
  // Fewer users than the response's `lowSampleUsers`.
  lowSample?: boolean;
}

// What "exclude the top user" removed (the uid stays server-side).
export interface ExcludedUser {
  moves: number;
  share: number | null;
}

// Stats filters: the connection's input mode ("text" = typed) and dropping
// the most active user.
export interface StatsFilters {
  inputMode: InputMode | null;
  excludeTopUser: boolean;
}

export interface LibraryTopicStats extends BalancedRates {
  topicId: string;
  calls: number; // sockets — the app reconnects constantly
  conversations: number;
  users: number;
  evalScore: number | null;
  arms: Partial<Record<Arm, BalancedRates>>;
  excludedTopUser?: ExcludedUser | null;
}

export interface VersionArmStats extends BalancedRates {
  version: number | null;
  arm: Arm;
  calls: number;
  conversations: number;
  users: number;
  evalScore: number | null;
  sentiment: number | null;
  userTurns: number | null;
}

// One unit's moves served under one version × arm.
export interface UnitVersionStats extends OutcomeRates {
  version: number | null;
  arm: Arm;
  users: number;
}

export interface UnitStats extends OutcomeRates {
  unitId: string;
  label: string | null;
  moveTypes: string[];
  users: number;
  byVersion?: UnitVersionStats[];
}

// Share of conversations whose furthest logged stage reached `stage`, each
// user weighted equally; `conversations` is the raw count.
export interface StageReach {
  stage: string;
  conversations: number;
  reached: number | null;
}

export interface StageFunnelPart {
  conversations: number;
  users: number;
  stages: StageReach[];
}

export interface StageFunnel extends StageFunnelPart {
  arms: Partial<Record<Arm, StageFunnelPart>>;
}

export interface TopicStats {
  topicId: string;
  days: number;
  liveVersion: number | null;
  candidateVersion: number | null;
  rolloutPct: number;
  versions: VersionArmStats[];
  units: UnitStats[];
  funnel?: StageFunnel;
  inputMode?: InputMode | null;
  excludeTopUser?: boolean;
  excludedTopUser?: ExcludedUser | null;
  lowSampleUsers?: number;
}

export type UnitMatch = "verbatim" | "paraphrase" | "related" | "none";

// One logged guide move (talk_track_events), as the drill-down and the
// conversation timeline return it. No transcript text: the call drawer has it.
export interface TalkTrackEvent {
  callId: string | null;
  sessionId: string | null;
  uid: string;
  turnIndex: number;
  createdAt: string | null;
  topicId: string | null;
  version: number | null;
  arm: Arm | null;
  stage: string | null;
  moveType: string | null;
  unitId: string | null;
  outcome: string | null;
  unitMatch: UnitMatch | null;
  trackId: string | null;
  suggestedUnitId: string | null;
  // null: nothing was suggested (or the move predates the field).
  followedSuggestion: boolean | null;
  classifiedBy: string | null;
  directorMs: number | null;
  catchUp: boolean | null;
  factsFilled: number | null;
  safetySignal: string | null;
  isRepeat: boolean | null;
}

export type UnitCall = TalkTrackEvent;

export interface TimelineCall {
  callId: string;
  startedAt: string | null;
  endedAt: string | null;
  inputMode: InputMode | null;
  userTurns: number | null;
  talkTrackTopic: string | null;
  talkTrackArm: Arm | null;
  talkTrackStage: string | null;
  talkTrackSafety: string | null;
}

// A call's conversation: every connection (same user + conversation id)
// and every move logged on them, in order.
export interface ConversationTimeline {
  callId: string;
  conversationId: string | null;
  uid: string;
  calls: TimelineCall[];
  events: TalkTrackEvent[];
}

// ---- bench + simulation + copilot

export interface ConversationPlan {
  topicId: string | null;
  version: number | null;
  arm: Arm | null;
  stage: string;
  furthestStage: string;
  facts: Record<string, string>;
  unitsUsed: string[];
  lastMove: string | null;
  lastUnitId: string | null;
  nextMove: string | null;
  nextUnitId: string | null;
  guidance: string;
  readyForOffer: boolean;
  safetySignal: string;
  blockedUnits: string[];
  turnIndex: number;
}

export interface BenchTurnResult {
  sessionId: string;
  reply: string;
  briefUsed: string;
  plan: ConversationPlan;
  nextBrief: string;
}

export type SimulationStatus = "queued" | "running" | "completed" | "failed";

export interface SimulationResult {
  scenario: string;
  title: string;
  safety: boolean;
  passed: boolean;
  score: number;
  judgement: Record<string, unknown> & { notes?: string };
  transcript: { role: string; text: string }[];
  moves: { stage: string; move: string | null; unitId: string | null; safetySignal: string }[];
}

export interface Simulation {
  id: string;
  topicId: string;
  version: number;
  guideId: string;
  status: SimulationStatus;
  requestedBy: string | null;
  // Content the run exercised; compared with the version's contentHash.
  contentHash: string | null;
  results: SimulationResult[];
  summary: {
    scenarios: number;
    errors: string[];
    safetyScenarios: number;
    safetyPassed: number;
  } | null;
  safetyPassed: boolean | null;
  score: number | null;
  error: string | null;
  createdAt: string | null;
  finishedAt: string | null;
}

export interface UnitSuggestions {
  suggestions: string[];
  rationale: string;
}

export interface ImportResult {
  created: string[];
  // Existing topics that got the bundle as a new draft (updateExisting).
  updated?: string[];
  skipped: string[];
  // Owner guides this environment doesn't have (imported without an owner).
  ownersDropped?: string[];
}

export interface CreateTopicInput {
  id: string;
  name: string;
  kind?: TopicKind;
  category: string;
  description: string;
  sortOrder: number;
  requiresSafetyScreen: boolean;
  ownerGuideId?: string | null;
}

export type TopicPatch = Partial<
  Pick<
    TalkTrackTopic,
    "name" | "category" | "description" | "sortOrder" | "enabled" | "requiresSafetyScreen" | "ownerGuideId"
  >
>;

export function emptyContent(): TalkTrackContent {
  return {
    summary: "", entrySignals: [], tracks: [], explainers: [], modules: [], safetyScreens: [],
    patterns: [], bridge: "", closings: [],
  };
}
